/**
 * Strategy session application endpoint config.
 *
 * The destination is read from `VITE_STRATEGY_SESSION_URL` so applications can
 * be routed to whatever receives them (webhook, CRM, form service) without a
 * code change. If it isn't set, the form fails loudly with a clear message
 * instead of silently dropping a qualified lead.
 */
import { z } from "zod";

export const STRATEGY_SESSION_URL: string =
  (import.meta.env.VITE_STRATEGY_SESSION_URL as string | undefined)?.trim() || "";

/**
 * Resolve the endpoint at call time. In dev/preview builds an e2e harness may
 * set `window.__STRATEGY_SESSION_URL__` so Playwright can exercise the full
 * success/error flow without a real intake endpoint. Never honored in prod.
 */
export function resolveStrategySessionUrl(): string {
  if (import.meta.env.DEV && typeof window !== "undefined") {
    const override = (window as unknown as { __STRATEGY_SESSION_URL__?: string })
      .__STRATEGY_SESSION_URL__;
    if (typeof override === "string" && override.trim()) return override.trim();
  }
  return STRATEGY_SESSION_URL;
}

export const isStrategySessionUrlConfigured = () => resolveStrategySessionUrl().length > 0;

export const FOCUS_OPTIONS = [
  "1:1 Executive Coaching",
  "Keynote Speaking",
  "Organizational Consulting",
  "Full-Scope Advocacy (FSAP)",
  "Masterclass / Cohort",
  "Not sure yet",
] as const;

export const TIMELINE_OPTIONS = [
  "Immediate — within 2 weeks",
  "Soon — within 30 days",
  "Planning ahead — 60–90 days",
  "Exploring options",
] as const;

export const BUDGET_OPTIONS = [
  "Under $2,500",
  "$2,500 – $10,000",
  "$10,000 – $25,000",
  "$25,000+",
  "Need guidance on budget",
] as const;

export const DECISION_ROLE_OPTIONS = [
  "I decide",
  "I recommend, someone else approves",
  "Committee / board decision",
] as const;

const oneOf = <T extends readonly string[]>(options: T, message: string) =>
  z.string().refine((v) => (options as readonly string[]).includes(v), { message });

export const strategySessionSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(2, { message: "Please enter your full name." })
    .max(100, { message: "Name must be under 100 characters." }),
  email: z
    .string()
    .trim()
    .email({ message: "Enter a valid email address." })
    .max(255, { message: "Email must be under 255 characters." }),
  phone: z.string().trim().max(30, { message: "Phone must be under 30 characters." }).optional().or(z.literal("")),
  organization: z
    .string()
    .trim()
    .max(150, { message: "Keep this under 150 characters." })
    .optional()
    .or(z.literal("")),
  role: z.string().trim().max(100, { message: "Keep this under 100 characters." }).optional().or(z.literal("")),
  focus: oneOf(FOCUS_OPTIONS, "Select what you're exploring."),
  timeline: oneOf(TIMELINE_OPTIONS, "Select a timeline."),
  budget: oneOf(BUDGET_OPTIONS, "Select an investment range."),
  decisionRole: oneOf(DECISION_ROLE_OPTIONS, "Tell us who signs off."),
  situation: z
    .string()
    .trim()
    .min(20, { message: "Give us a sentence or two about the situation." })
    .max(2000, { message: "Keep this under 2000 characters." }),
  outcome: z
    .string()
    .trim()
    .min(10, { message: "What does success look like?" })
    .max(1000, { message: "Keep this under 1000 characters." }),
});

export type StrategySessionInput = z.infer<typeof strategySessionSchema>;

/** Hard ceiling on a hung intake request so the form can never stall forever. */
export const STRATEGY_SESSION_TIMEOUT_MS = 15_000;

export const TIMEOUT_MESSAGE =
  "That took too long to send — the intake service didn't respond. Your answers are saved below; please try again.";
export const OFFLINE_MESSAGE =
  "We couldn't reach the intake service. Check your connection — your answers are saved below, so you can try again.";

function resolveTimeoutMs(): number {
  if (import.meta.env.DEV && typeof window !== "undefined") {
    const override = (window as unknown as { __STRATEGY_SESSION_TIMEOUT_MS__?: number })
      .__STRATEGY_SESSION_TIMEOUT_MS__;
    if (typeof override === "number" && override > 0) return override;
  }
  return STRATEGY_SESSION_TIMEOUT_MS;
}

export async function submitStrategySession(
  payload: StrategySessionInput & Record<string, unknown>,
  signal?: AbortSignal,
): Promise<void> {
  if (!isStrategySessionUrlConfigured()) {
    throw new Error(
      "Applications aren't connected yet. Please email the team directly and we'll review your fit.",
    );
  }

  const controller = new AbortController();
  const timedOut = { value: false };
  const timer = setTimeout(() => {
    timedOut.value = true;
    controller.abort();
  }, resolveTimeoutMs());
  const onExternalAbort = () => controller.abort();
  signal?.addEventListener("abort", onExternalAbort);

  let res: Response;
  try {
    res = await fetch(resolveStrategySessionUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
  } catch (err) {
    if (timedOut.value) throw new Error(TIMEOUT_MESSAGE);
    if (signal?.aborted) throw err;
    // fetch rejects with a TypeError when the server is unreachable / DNS fails.
    throw new Error(OFFLINE_MESSAGE);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onExternalAbort);
  }

  if (!res.ok) {
    throw new Error(
      res.status >= 500
        ? "Our intake service hiccupped. Please try again in a moment."
        : "We couldn't submit that application. Check your details and try again.",
    );
  }
}

